import { Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { CountriesState } from '@nibtravel/shared/data-access/countries';
import { ITripSummary, QuoteState } from '@nibtravel/shared/data-access/quote';
import { ICustomDimensions } from '@nibtravel/shared/util/gtm';
import { DateUtil, IPolicyHoldersInput } from '@nibtravel/shared/util/models';
import { format } from 'date-fns';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DimensionHelper } from './dimension-helpers';

// TODO: Should make this a selector
@Injectable()
export class PurchasePathDimensions {
  @Select(QuoteState.getTripSummary)
  tripDetails$!: Observable<ITripSummary>;

  constructor(private store: Store) {}

  getDimension(): Observable<ICustomDimensions> {
    return combineLatest([this.tripDetails$, this.getDestinationInput()]).pipe(
      map(([tripDetail, countryList]) => {
        const destination = this.destinationName(countryList);

        const duration = DimensionHelper.getDuration(
          tripDetail.departureDate,
          tripDetail.coverEndDate
        );
        const leadTime = DimensionHelper.getDuration(
          format(new Date(), DateUtil.IsoDateFormat),
          tripDetail.departureDate
        );

        const ages = this.accumulatedAge(tripDetail.policyHolders);
        const ageGroups = this.accumulatedAgeGroup(tripDetail.policyHolders);

        const traveller = tripDetail.policyHolders
          ? tripDetail.policyHolders.filter((ph) => !ph.isDependent).length
          : 0;
        const dependent = tripDetail.policyHolders
          ? tripDetail.policyHolders.filter((ph) => ph.isDependent).length
          : 0;

        const quoteId = this.store.selectSnapshot(QuoteState.quote)?.id;
        const quoteType = this.store.selectSnapshot(QuoteState.quoteType);

        return {
          dimension1: destination,
          dimension2: duration,
          dimension3: leadTime,
          dimension4: ageGroups,
          dimension5: ages,
          dimension6: traveller,
          dimension7: dependent,
          dimension9: quoteId,
          dimension10: quoteType,
        };
      })
    );
  }

  private getDestinationInput(): Observable<string[]> {
    return combineLatest([
      this.tripDetails$,
      this.store.select(CountriesState.destinationCountriesList),
    ]).pipe(
      map(([tripDetail, countryList]) =>
        tripDetail.destinationCountryCodes
          ? tripDetail.destinationCountryCodes.map(
              (code) => countryList.find((c) => c.code === code)?.name || ''
            )
          : []
      )
    );
  }

  private destinationName(countries: string[]): string {
    return countries
      ? countries
          .filter((c) => !!c)
          .sort()
          .reduce(DimensionHelper.accumulateDimension, '')
      : '';
  }

  private accumulatedAge(policyHolders: IPolicyHoldersInput[]): string {
    return policyHolders
      ? (
          policyHolders
            .map((ph) => ph.age)
            .filter((age) => age !== null) as number[]
        ).reduce(DimensionHelper.accumulateDimension, '')
      : '';
  }
  private accumulatedAgeGroup(policyHolders: IPolicyHoldersInput[]): string {
    return policyHolders
      ? policyHolders
          .map((ph) => DimensionHelper.getAgeGroup(ph.age))
          .reduce(DimensionHelper.accumulateDimension, '')
      : '';
  }
}
